import { z } from 'zod';

const closeCustomFieldId = z.string().regex(/^cf_[A-Za-z0-9]+$/, 'must be a Close custom-field ID');
const closeOpportunityStatusId = z.string().regex(/^stat_[A-Za-z0-9]+$/, 'must be a Close opportunity status ID');
const fiopsLoanStageUuid = z.string().uuid('must be a fiops loan-stage UUID');

/**
 * Issue #1734 — opportunity → fiops loan bidirectional sync config. Keys map
 * Close opportunity status IDs (`stat_xxx`) onto fiops `LoanStage.id` (UUID).
 * Stages absent from the map are treated as no-op per Open Question 4.
 */
export const opportunityStageMappingSchema = z.record(closeOpportunityStatusId, fiopsLoanStageUuid);
export type OpportunityStageMappingInput = z.infer<typeof opportunityStageMappingSchema>;

export const saveCloseConfigInput = z.object({
  // Issue #1734 — apiKey is OPTIONAL once a config row exists. The original
  // contract required the operator to re-paste the key on every save; that
  // blocks downstream surfaces (opportunity sync, writeback toggle) from
  // saving without forcing a re-paste, even though the encrypted key is
  // already in `business_integration_configs.api_key`. The backend
  // (`CloseConfigService.saveConfig`) preserves the existing encrypted key
  // when this field is omitted. First-time setup still needs the key.
  apiKey: z.string().min(1).max(200).optional(),
  triggerCustomFieldId: closeCustomFieldId,
  triggerValue: z.string().min(1).max(200),
  entityTypeCustomFieldId: closeCustomFieldId.optional(),
  einCustomFieldId: closeCustomFieldId.optional(),
  /**
   * Issue #1655 — per-business toggle for outbound (fiops → Close) sync.
   * Stored inside `BusinessIntegrationConfig.config` JSON; default off so
   * existing tenants are not surprised by writes back to Close.
   */
  closeOutboundSyncEnabled: z.boolean().optional(),
  /**
   * Issue #1670 Phase 4a — writeback toggle + Close custom-field that
   * receives the fiops borrower-URL backlink. The borrower-URL field is
   * optional; tenant can leave it unset.
   */
  closeStatusWritebackEnabled: z.boolean().optional(),
  writebackBorrowerIdField: closeCustomFieldId.optional(),
  /**
   * Issue #1734 — opportunity → fiops loan sync. Trigger stage ID at which
   * we create a fiops loan. `opportunityStageMapping` covers ongoing
   * Close→fiops stage advances + the inverse (fiops→Close) writeback when
   * `opportunityWritebackEnabled` is true. `fiopsLoanUrlCustomFieldId`
   * (Round-2 #4) is the optional Close custom field we stamp with the
   * fiops loan-detail URL after creation. `opportunityFieldMap` maps
   * fiops Case field names (`propertyAddress`, `loanAmount`, etc.) to
   * Close opportunity custom-field IDs (`cf_xxx`) so the customer can
   * point each fiops field at the right Close custom field.
   */
  opportunityTriggerStageId: closeOpportunityStatusId.optional(),
  opportunityStageMapping: opportunityStageMappingSchema.optional(),
  /**
   * PR-feedback #3237963990 — REQUIRED so callers can never accidentally
   * silent-disable opportunity writeback by omitting the field. The earlier
   * `optional()` let an unrelated save (e.g. outbound-sync toggle) coerce
   * `undefined` → `false` over the wire and overwrite the user's stored
   * opportunity writeback toggle. Callers that don't want to touch
   * opportunity sync MUST round-trip the existing value (getConfig →
   * saveConfig) so intent is explicit.
   */
  opportunityWritebackEnabled: z.boolean(),
  fiopsLoanUrlCustomFieldId: closeCustomFieldId.optional(),
  opportunityFieldMap: z.record(z.string().min(1), closeCustomFieldId).optional(),
});

export type SaveCloseConfigInput = z.infer<typeof saveCloseConfigInput>;

export const testCloseConnectionInput = z.object({
  apiKey: z.string().min(1).max(200),
});

export type TestCloseConnectionInput = z.infer<typeof testCloseConnectionInput>;

export const refreshConsumerInput = z.object({
  consumerId: z.string().uuid(),
});

export type RefreshConsumerInput = z.infer<typeof refreshConsumerInput>;

export const mergeConsumersInput = z.object({
  primaryConsumerId: z.string().uuid(),
  secondaryConsumerId: z.string().uuid(),
  fieldChoices: z.record(z.string(), z.string()),
  candidateId: z.string().uuid().optional(),
});

export type MergeConsumersInput = z.infer<typeof mergeConsumersInput>;

export const listSyncedConsumersInput = z.object({
  source: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(25),
  cursor: z.string().optional(),
});

export type ListSyncedConsumersInput = z.infer<typeof listSyncedConsumersInput>;

export const createApplicationFromConsumerInput = z.object({
  consumerId: z.string().uuid(),
  templateId: z.string().uuid(),
});

export type CreateApplicationFromConsumerInput = z.infer<typeof createApplicationFromConsumerInput>;

export const updateIntakeSubmissionInput = z.object({
  id: z.string().uuid(),
  formDataPatch: z.record(z.string(), z.unknown()),
});

export type UpdateIntakeSubmissionInput = z.infer<typeof updateIntakeSubmissionInput>;

export const submitIntakeSubmissionInput = z.object({
  id: z.string().uuid(),
});

export type SubmitIntakeSubmissionInput = z.infer<typeof submitIntakeSubmissionInput>;

export const dismissDuplicateCandidateInput = z.object({
  candidateId: z.string().uuid(),
});

export type DismissDuplicateCandidateInput = z.infer<typeof dismissDuplicateCandidateInput>;

// ============================================================================
// Per-tenant Close custom-field ↔ fiops field mapping (Issue #1621 Stream 4e,
// extended in Issue #1655 to support tenant-defined FieldDefinition targets and
// bidirectional sync)
// ============================================================================

/**
 * Allowed `fiopsTarget` shapes for a Close custom-field mapping.
 *
 * Kept in sync with `FIOPS_TARGET_REGEX` in
 * `3-integration-service/src/connectors/close/close-field-mapping.service.ts`.
 *
 * Five forms accepted:
 *   - `borrowing_entity.<scalar>` — first-class column on BorrowingEntity
 *     (`legal_name | ein | dba_name | state_of_formation | formation_date | registered_agent`)
 *   - `borrowing_entity.metadata.<key>` — JSON metadata patch on BorrowingEntity (legacy)
 *   - `consumer.metadata.<key>` — JSON metadata patch on Consumer (legacy; only valid
 *     when `closeObjectType='contact'`; the backend rejects consumer.* targets
 *     paired with `closeObjectType='lead'`).
 *   - `<entity>.field.<uuid>` — tenant-defined FieldDefinition on a system entity
 *     (`borrower | borrowing_entity | loan | application`). The uuid is the
 *     `FieldDefinition.id` on the EntityDefinition row for that system entity.
 *
 * `<key>` follows snake_case: `[a-z_][a-z0-9_]*`.
 */
export const closeFieldMappingTargetSchema = z
  .string()
  .regex(
    /^(borrowing_entity\.(legal_name|ein|dba_name|state_of_formation|formation_date|registered_agent)|borrowing_entity\.metadata\.[a-z_][a-z0-9_]*|consumer\.metadata\.[a-z_][a-z0-9_]*|(borrower|borrowing_entity|loan|application)\.field\.[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})$/,
    'fiopsTarget must be borrowing_entity.<scalar> | borrowing_entity.metadata.<snake_case_key> | consumer.metadata.<snake_case_key> | <entity>.field.<uuid>',
  );

export type CloseFieldMappingTarget = z.infer<typeof closeFieldMappingTargetSchema>;

const closeObjectTypeSchema = z.enum(['contact', 'lead']);

/**
 * Sync direction enum (matches the Prisma `CloseSyncDirection` enum).
 *
 * - `INBOUND`        — Close → fiops only. Default (preserves existing behavior).
 * - `OUTBOUND`       — fiops → Close only.
 * - `BIDIRECTIONAL`  — both, with `conflictResolution` deciding ties.
 *
 * Backward-compat: the legacy literal `'close_to_fiops'` is accepted at the
 * input boundary and transformed to `'INBOUND'`. Service-layer code only
 * needs to handle the upper-case forms.
 */
export const closeSyncDirectionSchema = z
  .union([
    z.enum(['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL']),
    z.literal('close_to_fiops').transform(() => 'INBOUND' as const),
  ])
  .pipe(z.enum(['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL']));

export type CloseSyncDirection = 'INBOUND' | 'OUTBOUND' | 'BIDIRECTIONAL';

/**
 * Conflict-resolution rule for `BIDIRECTIONAL` mappings.
 *
 * - `LAST_WRITE_WINS` — most recent `updated_at` wins (default).
 * - `FIOPS_WINS`      — fiops always pushes, Close inbound is ignored on conflict.
 * - `CLOSE_WINS`      — Close webhook always overwrites; fiops never pushes outbound.
 */
export const closeConflictRuleSchema = z.enum(['LAST_WRITE_WINS', 'FIOPS_WINS', 'CLOSE_WINS']);

export type CloseConflictRule = z.infer<typeof closeConflictRuleSchema>;

/**
 * Choice value-map: `{ closeOptionId: fiopsOptionValue }` for SELECT/MULTI_SELECT
 * mappings. `closeOptionId` is the choice's UUID/string id from Close;
 * `fiopsOptionValue` is the chosen `option.value` on the fiops FieldDefinition.
 */
export const closeChoiceValueMapSchema = z.record(z.string().min(1).max(200), z.string().min(1).max(500));

export type CloseChoiceValueMap = z.infer<typeof closeChoiceValueMapSchema>;

/**
 * Allowed `fiopsFieldType` values. Accepts:
 *   - Legacy lowercase forms (`string | number | date | enum`) for
 *     backward-compat with existing rows.
 *   - The full `EntityFieldType` enum (uppercase) for tenant-defined
 *     FieldDefinition targets.
 *
 * The integration-service `close-type-compat` module is the source of truth
 * for which (CloseType, FieldType) pairs are actually compatible — this
 * schema only enforces the syntactic surface.
 */
export const closeFiopsFieldTypeSchema = z.enum([
  // legacy (kept for back-compat with existing mappings)
  'string',
  'number',
  'date',
  'enum',
  // EntityFieldType (matches `1-shared-types/src/enums/entity.enums.ts`)
  'TEXT',
  'TEXTAREA',
  'RICH_TEXT',
  'NUMBER',
  'CURRENCY',
  'PERCENTAGE',
  'DATE',
  'DATETIME',
  'TIME',
  'BOOLEAN',
  'SELECT',
  'MULTI_SELECT',
  'LOOKUP',
  'MULTI_LOOKUP',
  'EMAIL',
  'PHONE',
  'URL',
  'ADDRESS',
  'AUTO_NUMBER',
  'FILE',
  'IMAGE',
  'JSON',
  'CALCULATED',
  'ROLLUP',
  'RATING',
  'SIGNATURE',
]);

export type CloseFiopsFieldType = z.infer<typeof closeFiopsFieldTypeSchema>;

/**
 * Save (upsert) a Close field mapping.
 *
 * Issue #1655: `direction` accepts INBOUND/OUTBOUND/BIDIRECTIONAL (defaults to
 * INBOUND); `conflictResolution` only consulted when direction=BIDIRECTIONAL;
 * `choiceValueMap` only consulted for SELECT/MULTI_SELECT targets.
 */
export const saveCloseFieldMappingInput = z.object({
  closeObjectType: closeObjectTypeSchema,
  closeCustomFieldId: z.string().regex(/^cf_[A-Za-z0-9]+$/, 'must be a Close custom-field ID'),
  closeFieldLabel: z.string().max(200).optional(),
  fiopsTarget: closeFieldMappingTargetSchema,
  fiopsFieldType: closeFiopsFieldTypeSchema,
  enabled: z.boolean().default(true),
  /** Sync direction — defaults to INBOUND (existing behavior). */
  direction: closeSyncDirectionSchema.default('INBOUND'),
  /** Conflict resolution rule — only consulted when direction=BIDIRECTIONAL. */
  conflictResolution: closeConflictRuleSchema.default('LAST_WRITE_WINS'),
  /**
   * Optional value-map for SELECT/MULTI_SELECT mappings:
   * `{ <closeChoiceId>: <fiopsOptionValue> }`. Unmapped Close options surface
   * as warnings in the UI; the integration service skips them at write time.
   */
  choiceValueMap: closeChoiceValueMapSchema.optional(),
});

export type SaveCloseFieldMappingInput = z.infer<typeof saveCloseFieldMappingInput>;

export const listCloseFieldMappingsInput = z
  .object({
    closeObjectType: closeObjectTypeSchema.optional(),
  })
  .optional();

export type ListCloseFieldMappingsInput = z.infer<typeof listCloseFieldMappingsInput>;

export const deleteCloseFieldMappingInput = z.object({
  id: z.string().uuid(),
});

export type DeleteCloseFieldMappingInput = z.infer<typeof deleteCloseFieldMappingInput>;

/**
 * Compatibility check input for the `closeIntegration.fieldMappings.checkCompatibility`
 * query. The UI calls this as soon as the user has picked both sides of a
 * mapping so the form can block save with a typed error before the round-trip.
 */
export const checkCloseCompatibilityInput = z.object({
  /** Close field type as returned by `getCustomFieldSchema` (`text`, `number`, `date`, `choices`, `multiple_choice`, `contact`, `user`). */
  closeFieldType: z.string().min(1).max(64),
  /** Either a literal target (`borrowing_entity.legal_name`) or a tenant target (`<entity>.field.<uuid>`). */
  fiopsTarget: closeFieldMappingTargetSchema,
  /** Resolved `EntityFieldType` for the picked target (only required for `<entity>.field.<uuid>` targets). */
  fiopsFieldType: closeFiopsFieldTypeSchema.optional(),
});

export type CheckCloseCompatibilityInput = z.infer<typeof checkCloseCompatibilityInput>;

export const checkCloseCompatibilityOutput = z.object({
  compatible: z.boolean(),
  /** Human-readable reason the mapping was rejected (only set when `compatible=false`). */
  reason: z.string().optional(),
});

export type CheckCloseCompatibilityOutput = z.infer<typeof checkCloseCompatibilityOutput>;

// ============================================================================
// Issue #1738 — Webhook-only sync UI + DLQ inputs
// ============================================================================

/**
 * Lowercase audit-direction enum — distinct from `closeSyncDirectionSchema`
 * (UPPERCASE) which describes field-mapping config. This one labels a
 * specific persisted audit entry.
 */
export const closeSyncAuditDirectionSchema = z.enum(['inbound', 'outbound']);

export const closeSyncTriggerSourceSchema = z.enum(['webhook_event', 'user_initiated', 'dlq_drain', 'cron_drain']);

/** Input shape for `closeIntegration.getWebhookHealth` (per-business, queried frequently). */
export const getCloseWebhookHealthInput = z.object({}).optional();
export type GetCloseWebhookHealthInput = z.infer<typeof getCloseWebhookHealthInput>;

/** Input shape for `closeIntegration.getDlqCounts` (per-business). */
export const getCloseDlqCountsInput = z.object({}).optional();
export type GetCloseDlqCountsInput = z.infer<typeof getCloseDlqCountsInput>;

/**
 * Input shape for `closeIntegration.drainInboundDlq`. Empty body — drain
 * is per-business and authorized by the operator's businessId on the
 * server side. Permission gate (`Permission.integration.manage`) is
 * enforced at the tRPC router; the empty shape exists so we still have
 * a zod schema to attach for runtime validation of future arguments
 * (e.g. `eventType` filter).
 */
export const drainCloseInboundDlqInput = z.object({}).optional();
export type DrainCloseInboundDlqInput = z.infer<typeof drainCloseInboundDlqInput>;

/**
 * Direction filter for the audit-page UI. `'all'` is the default; the
 * other two are narrow.
 */
export const auditDirectionFilterSchema = z.enum(['all', 'inbound', 'outbound']);
export type AuditDirectionFilter = z.infer<typeof auditDirectionFilterSchema>;
